/**
 * lib/fis-factors.ts — THE one description of what the (FIS) Fridge.com
 * Intelligence Score actually computes. One version, everywhere.
 *
 * Companion to lib/identifiers.ts: that module owns the canonical DEFINITION
 * of the FIS ("a 0–100 rating of how well every U.S. state, city, and ZIP code
 * supports efficient, affordable kitchen operation"); this module owns the
 * MECHANICS that a surface may append after it. Both are imported, never
 * retyped — FRIDGE-381 exists because the mechanics were retyped on four pages
 * and drifted into fiction on all four.
 *
 * THE RULING. Item 109(7) on docs/MASTER-PLAN.md, decided by the owner
 * 07/20/2026: "FIS pseudo-factors — REJECTED: FIS stays honest-signal-only …
 * Grid-Efficiency-adoption / Nutritional Velocity / Kitchen-Economics-grocery /
 * CDC / ACSM / C2ER claims all drop." Those sub-factors were never inputs. The
 * strings below are read out of the two scripts that write the scores, and
 * tests/fis-ruled-out-pseudo-factors.test.ts fails if the invented ones return.
 *
 * TWO SCORES, TWO FORMULAS — do not describe one with the other's inputs.
 *
 *   City — fis_locations, written by calculateFisScore() in
 *   scripts/regenerate-fis-scores.ts (lines 49-86):
 *       score = rateUrgency(2-40) + climateUrgency(5-25)
 *             + incomeTierWeight(10-28, unknown 15) + rebateBonus(0 or 5).
 *       The code clamps to 0-100; these components can reach at most 98.
 *   State pages average city/location scores; they do not run that formula
 *   on the state electricity rate. Unsupported rows must not enter the mean.
 *
 *   ZIP — zip_energy_data, written by calculateFIS() in
 *   scripts/import-zip-energy-data.js (lines 53-62):
 *       score = 100 - ((rate - 0.10) * 200) ± a 2-3 point climate adjustment,
 *       clamped to 70-98.
 *
 * The two run in OPPOSITE directions on the same input: a high city score means
 * expensive power (an efficient fridge matters most there), a high ZIP score
 * means cheap power. That is why no string here claims a direction that is not
 * true of the scope it describes.
 *
 * THE DIRECTION DEFECT — the sibling of FRIDGE-381, fixed 08/22/2026. Naming
 * the right factors was only half of it: the shipped copy also asserted ONE
 * direction on surfaces that cover both scopes, so /about-us/fis (the
 * definitional home for states, cities AND ZIPs) told readers "higher is
 * cheaper" — true of a ZIP, false of every city and state — while the /local
 * hub FAQ and public/llms.txt asserted the opposite, equally unqualified.
 * Every direction sentence here now names the scope it is true of, and
 * FIS_DIRECTION_BY_SCOPE is the only string a cross-scope surface may use.
 * tests/fis-direction-per-scope.test.ts is the red check, and it also pins
 * both directions against the two scripts above, so flipping a formula turns
 * the copy red instead of quietly wrong.
 *
 * FRIDGE-399/476 owner decisions authorize honest reconciliation while
 * preserving the canonical definition and explaining these limitations.
 * They do not specify a replacement formula or authorize inventing one.
 * Existing city inputs use a selected ZIP utility rate/climate tuple, not a
 * city average. Income can use a fixed default and a stored rebate bonus is
 * not evidence of current program eligibility. New state monthly rates do
 * not change the separate utility-rate inputs to the existing city/ZIP scores.
 */

import { identifierDefinitionBody } from './identifiers'

export interface FisFactor {
  /** The component's name, as the scoring code names it. */
  name: string
  /** Shopper-facing description of the real signal behind it. */
  description: string
  /** The component's maximum contribution to the score. */
  maxPoints: number
}

/**
 * The four components of a CITY score (fis_locations). State summaries
 * average supported city scores, so these explain their underlying method.
 * Names match the scoring code and the already-cleaned index FAQ
 * (lib/local/local-index-faqs.ts) so no two surfaces word them differently.
 */
export const FIS_LOCATION_FACTORS: readonly FisFactor[] = [
  {
    name: 'Rate urgency',
    description:
      '2–40 points from the selected utility-rate estimate stored for the city. Higher rates add more urgency points; this input is not a city average or a household tariff.',
    maxPoints: 40,
  },
  {
    name: 'Climate urgency',
    description:
      '5–25 points from the stored climate zone, assigned by a state and coordinate heuristic. This is a model adjustment, not measured refrigerator energy use.',
    maxPoints: 25,
  },
  {
    name: 'Savings impact',
    description:
      'An income-tier weight: 28 points for budget, 18 for mid, 10 for premium, or a fixed 15 when the tier is unknown. Despite the component name, estimated dollar savings are not an input to this calculation.',
    maxPoints: 28,
  },
  {
    name: 'Rebate bonus',
    description:
      'A stored 5-point bonus from the utility-rebate availability check used when the score was generated. It does not confirm that a program is currently open or that a household or purchase qualifies.',
    maxPoints: 5,
  },
] as const

/** The two signals a ZIP score is built from (zip_energy_data). */
export const FIS_ZIP_FACTORS: readonly FisFactor[] = [
  {
    name: 'Electricity rate',
    description:
      'The selected utility-average rate estimate associated with the ZIP sets the score — ZIP codes with cheaper power score higher.',
    maxPoints: 98,
  },
  {
    name: 'Climate zone',
    description:
      'A small model adjustment using the state and coordinate climate heuristic: up in cold and marine zones, down in hot-humid and hot-dry ones.',
    maxPoints: 3,
  },
] as const

/** The canonical FIS definition, from lib/identifiers.ts. Never retyped. */
export const FIS_CANONICAL_DEFINITION = identifierDefinitionBody('FIS')

/** Lead sentence for a city or state explainer, before the four components. */
export const FIS_LOCATION_EXPLAINER =
  `${FIS_CANONICAL_DEFINITION} The current city formula measures upgrade urgency: a city scores higher with a higher stored electricity rate, climate adjustment, income-tier weight, or historical rebate bonus. A state summary averages supported city scores; it is not calculated from the state rate. The city formula has four components:`

/** Lead sentence for a ZIP explainer, before the two inputs. */
export const FIS_ZIP_EXPLAINER =
  'A ZIP code’s (FIS) Fridge.com Intelligence Score uses a utility-average rate estimate associated with the ZIP and a climate-zone heuristic. A ZIP code scores higher where power is cheaper, opposite to the city urgency formula. ZIP scores are clamped to 70–98 and do not measure a household’s full kitchen costs.'

/**
 * THE DIRECTION, stated per scope — the one sentence a surface spanning both
 * scopes may use. Never assert a single direction across all three scopes:
 * the two formulas above run opposite ways on the same input, so any such
 * sentence is false for one of them. tests/fis-direction-per-scope.test.ts is
 * the red check. public/llms.txt carries this string verbatim because it
 * cannot import.
 */
export const FIS_DIRECTION_BY_SCOPE =
  'The city and ZIP formulas run in opposite directions: a city scores higher with greater modeled upgrade urgency, while a ZIP code scores higher where power is cheaper. A state summary averages supported city scores. Scores are not directly comparable across these scopes.'

/** One-sentence summary safe on any surface that spans states, cities and ZIPs. */
export const FIS_SCOPE_SUMMARY =
  'City scores use a selected utility-rate estimate, a climate heuristic, an income-tier weight with a fixed default, and a historical rebate bonus. ZIP scores use a utility-rate estimate and climate heuristic; state summaries average supported city scores. These methods do not establish the total cost of kitchen operation.'

/**
 * The inline prose an FAQ answer uses to list the real components. Defined
 * once here so the FAQ wording and the explainer cards can never disagree.
 */
export const FIS_LOCATION_COMPONENT_SENTENCE =
  'The city score adds rate urgency (2–40 points from the selected utility electricity rate estimate), climate urgency (5–25 from a climate zone heuristic), an income-tier weight (10–28, or a fixed 15 when unknown), and a historical rebate bonus (0 or 5). Estimated dollar savings are not a score input. The code clamps the sum to 0–100; the current components can reach 98. A state summary averages supported city scores, and a stored bonus does not confirm current rebate eligibility.'

export const FIS_ZIP_COMPONENT_SENTENCE =
  'A ZIP code’s score uses a utility-average rate estimate associated with that ZIP and a small adjustment from a climate-zone heuristic. The current formula clamps the result to 70–98.'

/**
 * The factor list for a given scope. ZIP scores genuinely use fewer inputs, so
 * a ZIP surface must never be handed the four-component list.
 */
export function fisFactorsFor(scope: 'state' | 'city' | 'zip'): readonly FisFactor[] {
  return scope === 'zip' ? FIS_ZIP_FACTORS : FIS_LOCATION_FACTORS
}

/** Prose list of the real component names, e.g. for a single-sentence answer. */
export function fisFactorNames(scope: 'state' | 'city' | 'zip'): string {
  const names = fisFactorsFor(scope).map((f) => f.name.toLowerCase())
  return names.length === 2
    ? `${names[0]} and ${names[1]}`
    : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}
